import React, { useEffect, useState } from 'react';
import { Field, FieldProps } from '@atlaskit/form';
import { token } from '@atlaskit/tokens';
import Textfield from '@atlaskit/textfield';
import SearchIcon from '@atlaskit/icon/glyph/search';
import SelectClearIcon from '@atlaskit/icon/glyph/select-clear';
import Button from '@atlaskit/button';
import { N90 } from '@atlaskit/theme/colors';
import { RecentlyViewedDrawer } from '@css-xp/ppc-home-page';
import { SEARCH } from './constants/search';
import { searchFieldKey } from './search-form';
import './search-form.scss';

interface SearchInputContainerProps {
  defaultValue?: string;
  className?: string;
  placeHolderText?: string;
  isClearable?: boolean;
  onReset?: () => void;
  showRightSearchIcon?: boolean;
  searchIconSize?: 'small' | 'medium' | 'large' | 'xlarge';
  showSearchMenu?: boolean;
  txaId?: string;
  shouldClearSearchInput?: boolean;
}

const MINIMUM_LENGTH_SEARCH_TERM = 3;

export const SearchInputContainer = ({
  defaultValue,
  className,
  placeHolderText,
  isClearable = false,
  onReset,
  showRightSearchIcon,
  searchIconSize,
  showSearchMenu,
  txaId,
  shouldClearSearchInput,
}: SearchInputContainerProps) => {
  const [fieldValue, setFieldValue] = useState<string>(defaultValue || '');
  const [isFocused, setIsFocused] = useState<boolean>(false);

  useEffect(() => {
    if (defaultValue) {
      setFieldValue(defaultValue);
    }
  }, [defaultValue]);

  useEffect(() => {
    if (shouldClearSearchInput) {
      setFieldValue('');
    }
  }, [shouldClearSearchInput]);

  const searchInputContainerClassname = `${className ?? 'search-input-container'}`;
  const renderSearchIconWrapper = () => {
    const wrapperClassNames = showRightSearchIcon ? 'partner-search-icon-wrapper-right' : 'partner-search-icon-wrapper';

    return (
      <div className={wrapperClassNames} data-testid={wrapperClassNames}>
        <SearchIcon label="search-icon" size={searchIconSize ?? 'small'} primaryColor={token('color.icon.subtle')} />
      </div>
    );
  };

  const handleClick = () => {
    if (showSearchMenu) {
      setIsFocused(true);
    }
  };

  const handleBlur = () => {
    if (showSearchMenu) {
      // setTimeout is used to prevent the drawer from closing and stopping the user from clicking on the links
      // To improve: find a better way to handle this
      setTimeout(() => {
        setIsFocused(false);
      }, 150);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (showSearchMenu && e.key === 'Enter') {
      setIsFocused(false);
    }
  };

  return (
    <div className={searchInputContainerClassname}>
      <Field name={searchFieldKey} defaultValue={defaultValue} isRequired>
        {({ fieldProps }: { fieldProps: FieldProps<string> }) => (
          <Textfield
            {...fieldProps}
            className="partner-search-field"
            isCompact={false}
            role="search"
            value={fieldValue}
            onChange={(e) => {
              if (showSearchMenu && !isFocused) {
                setIsFocused(true);
              }
              setFieldValue(e.currentTarget.value);
              fieldProps.onChange(e);
            }}
            onClick={handleClick}
            onBlur={handleBlur}
            onKeyDown={handleKeyDown}
            data-testId="partner-search-field"
            aria-label={placeHolderText ?? SEARCH.PLACEHOLDER}
            placeholder={placeHolderText ?? SEARCH.PLACEHOLDER}
            elemBeforeInput={showRightSearchIcon || renderSearchIconWrapper()}
            elemAfterInput={
              <>
                {isClearable && fieldValue.length !== 0 ? (
                  <Button
                    appearance="subtle"
                    className="partner-search-clear"
                    testId="partner-search-clear"
                    onClick={() => {
                      setFieldValue('');
                      onReset && onReset();
                    }}
                    iconBefore={<SelectClearIcon primaryColor={N90} label="" size="small" />}
                  ></Button>
                ) : null}
                {showRightSearchIcon && renderSearchIconWrapper()}
              </>
            }
            minLength={MINIMUM_LENGTH_SEARCH_TERM}
            autoComplete="off"
          />
        )}
      </Field>
      {/* submits the form on enter */}
      <input data-testid="search-submit-input" type="submit" style={{ display: 'none' }} />
      {isFocused && showSearchMenu && <RecentlyViewedDrawer searchInput={fieldValue} txaId={txaId || ''} />}
    </div>
  );
};
